import { useCreateInvoiceMutation } from "./useCreateInvoiceMutation";
import { ICreateInvoicePayload } from "../interfaces/invoice.interfaces";
import { useGlobalSnackbar } from "../../../../context/SnackbarContext";
import { AxiosError } from "axios";
import { useModals } from "../../../../context/ModalContext";
import { useQueryClient } from "@tanstack/react-query";

/**
 * Custom hook for creating invoices
 * Encapsulates the mutation logic, error handling, and success notifications
 */
export const useCreateInvoice = () => {
  const createInvoiceMutation = useCreateInvoiceMutation();
  const { showSnackbar } = useGlobalSnackbar();
  const { closeByKey } = useModals();
  const qc = useQueryClient();
  /**
   * Get user-friendly error message from error object
   */
  const getErrorMessage = (error: unknown): string => {
    if (error instanceof AxiosError) {
      const status = error.response?.status;
      const message = error.response?.data?.message || error.message;
      const paypalErrorDetails = error.response?.data?.data?.details?.[0]?.issue || error.response?.data?.data?.message;

      // Handle specific PayPal errors if available
      if (paypalErrorDetails) {
        return `PayPal Error: ${paypalErrorDetails}`;
      }

      // Handle different error status codes
      switch (status) {
        case 400:
          return message || "Invalid invoice data. Please check your input and try again.";
        case 401:
          return "You are not authorized to create invoices. Please log in again.";
        case 403:
          return "You don't have permission to create invoices for this company.";
        case 404:
          return message || "Required resources not found. Please verify scope of work and job event.";
        case 409:
          return (
            message || "One or more visits are already on an active invoice. Remove them from the selection or cancel/refund the existing invoice."
          );
        case 422:
          return message || "Validation error. Please check all required fields.";
        case 500:
          return "Server error occurred. Please try again later or contact support.";
        case 502:
          return message || "The invoice could not be sent by email, so it was not created. Please try again.";
        case 503:
          return "Service is temporarily unavailable. Please try again later.";
        default:
          return message || "An unexpected error occurred. Please try again.";
      }
    }

    if (error instanceof Error) {
      return error.message;
    }

    return "An unexpected error occurred. Please try again.";
  };

  /**
   * Create invoice with automatic error handling and notifications
   */
  const createInvoice = (invoice: ICreateInvoicePayload, options?: { invoiceModal?: string }) => {
    createInvoiceMutation.mutate(invoice, {
      onSuccess: (data) => {
        showSnackbar("Invoice created successfully! Payment link has been generated.", "success", 5000, "top-center");
        closeByKey(options?.invoiceModal!);
        qc.invalidateQueries({ queryKey: ["invoice-metrics"] });
        qc.invalidateQueries({ queryKey: ["invoice-list"] });
      },
      onError: (error) => {
        console.error("Error creating invoice:", error);
        const errorMessage = getErrorMessage(error);
        showSnackbar(errorMessage, "error", 6000, "top-center");
      },
    });
  };

  return {
    createInvoice,
    isPending: createInvoiceMutation.isPending,
    isError: createInvoiceMutation.isError,
    isSuccess: createInvoiceMutation.isSuccess,
    error: createInvoiceMutation.error,
    data: createInvoiceMutation.data,
  };
};
